import { ApplicationRef, ComponentRef, EnvironmentInjector, Injectable, createComponent, inject } from "@angular/core";
import { PdfPreviewComponent } from "../components/pdf-preview/pdf-preview.component";
import { PdfPreviewConfig } from "../interfaces/pdf-preview.interfaces";

/**
 * Service that opens and closes a PDF preview overlay by dynamically creating
 * {@link PdfPreviewComponent} and attaching it to the Angular application.
 *
 * Guarantees a single active instance — calling {@link open} closes any existing preview first.
 */
@Injectable()
export class PdfPreviewService {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private cmpRef?: ComponentRef<PdfPreviewComponent>;

  open(config: PdfPreviewConfig, hostEl: HTMLElement = document.body): ComponentRef<PdfPreviewComponent> {
    this.close();

    const ref = createComponent(PdfPreviewComponent, {
      environmentInjector: this.envInjector,
    });
    this.cmpRef = ref;

    const openedSub = ref.instance.opened.subscribe(opened => {
      if (!opened) this.close();
    });

    ref.onDestroy(() => {
      openedSub.unsubscribe();
      if (this.cmpRef === ref) this.dispose();
    });

    if (config.file !== undefined) ref.setInput("file", config.file);
    if (config.resourcePipeOptions !== undefined) ref.setInput("resourcePipeOptions", config.resourcePipeOptions);
    if (config.resolveUrl !== undefined) ref.setInput("resolveUrl", config.resolveUrl);

    ref.setInput("opened", true);

    this.appRef.attachView(ref.hostView);

    const effectiveHost = hostEl?.isConnected ? hostEl : document.body;
    effectiveHost.appendChild(ref.location.nativeElement);

    ref.changeDetectorRef.detectChanges();

    return ref;
  }

  close(): void {
    this.dispose();
  }

  private dispose(): void {
    if (!this.cmpRef) return;

    this.appRef.detachView(this.cmpRef.hostView);
    this.cmpRef.destroy();
    this.cmpRef = undefined;
  }
}
